// src/generators/dzial1_liczby_i_dzialania/temat1_dzialania/podtemat3_zbiory_types/ListDefinedSetElementsTask.js
import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import FiniteSetInput, { parseFiniteSetInput, finiteSetToLatex } from '../../../../components/inputs/FiniteSetInput';

const rand = (a, b) => a + Math.floor(Math.random() * (b - a + 1));
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

/* ===== generator =====
   Proporcje: integers ~60%, naturals ~40%.

   Integers – cztery typy:
     LI_RE:  L ≤ x < R    → elems: L..R-1        (size = R-L)
     LE_RI:  L < x ≤ R    → elems: L+1..R        (size = R-L)
     LI_RI:  L ≤ x ≤ R    → elems: L..R          (size = R-L+1)
     LE_RE:  L < x < R    → elems: L+1..R-1      (size = R-L-1)

   Zawsze 3–6 elementów. Elementy muszą mieścić się w [-9, 9].
*/
export function generateTask() {
  const variant = Math.random() < 0.6 ? 'integers' : 'naturals';
  const symbol = pick(['A', 'B', 'S']);

  if (variant === 'integers') {
    const size = rand(3, 6);
    const phrasing = pick(['LI_RE', 'LE_RI', 'LI_RI', 'LE_RE']);

    let L, R, elems;

    if (phrasing === 'LI_RE') {
      // elems: L..R-1, size = R-L  ⇒ R = L + size
      // warunki na elementy: L ≥ -9 oraz R-1 ≤ 9 ⇒ R ≤ 10
      // ⇒ L ∈ [-9, 10 - size]
      L = rand(-9, 10 - size);
      R = L + size;
      elems = Array.from({ length: size }, (_, i) => L + i);
    } else if (phrasing === 'LE_RI') {
      // elems: L+1..R, size = R-L ⇒ R = L + size
      // warunki: L+1 ≥ -9 ⇒ L ≥ -10 oraz R ≤ 9 ⇒ L ≤ 9 - size
      // przycinamy do „ładnych” granic: L ∈ [-9, 9 - size] (wystarczy do elementów w [-9,9])
      L = rand(-9, 9 - size);
      R = L + size;
      elems = Array.from({ length: size }, (_, i) => L + 1 + i);
    } else if (phrasing === 'LI_RI') {
      // elems: L..R, size = R-L+1 ⇒ R = L + size - 1
      // warunki: L ≥ -9, R ≤ 9 ⇒ L ∈ [-9, 10 - size]
      L = rand(-9, 10 - size);
      R = L + size - 1;
      elems = Array.from({ length: size }, (_, i) => L + i);
    } else { // 'LE_RE'
      // elems: L+1..R-1, size = R-L-1 ⇒ R = L + size + 1
      // warunki: L+1 ≥ -9 ⇒ L ≥ -10; R-1 ≤ 9 ⇒ R ≤ 10 ⇒ L ≤ 9 - size
      // znów bierzemy L ∈ [-9, 9 - size] (granica R może być 10, element max 9)
      L = rand(-9, 9 - size);
      R = L + size + 1;
      elems = Array.from({ length: size }, (_, i) => L + 1 + i);
    }

    return {
      type: 'ListDefinedSetElementsTask',
      data: { variant, symbol, L, R, elems, phrasing, size },
    };
  }

  // ===== naturals (od 0) – ~40% =====
  const size = rand(3, 6);
  const strict = Math.random() < 0.5; // n < K (strict) albo n ≤ K (inclusive)
  // dobieramy bound tak, by liczność = size:
  //  - n < K  → {0..K-1}  → size = K       ⇒ bound = size
  //  - n ≤ K  → {0..K}    → size = K + 1   ⇒ bound = size - 1
  const bound = strict ? size : size - 1;
  const elems = Array.from({ length: size }, (_, i) => i);

  return {
    type: 'ListDefinedSetElementsTask',
    data: { variant, symbol, strict, bound, elems, size },
  };
}

/* ===== render treści ===== */
export function renderTask({ data }) {
  const { variant, symbol } = data;

  return (
    <div className="space-y-2">
      <p><strong>Wypisz wszystkie elementy zbioru</strong> <InlineMath math={symbol} />.</p>
      {variant === 'integers' ? <IntegersText data={data} /> : <NaturalsText data={data} />}
    </div>
  );
}

function IntegersText({ data }) {
  const { L, R, phrasing, symbol } = data;

  if (phrasing === 'LI_RE') {
    // L ≤ x < R
    return (
      <p>
        Zbiór <InlineMath math={symbol} /> jest zbiorem liczb całkowitych{' '}
        <strong>nie mniejszych</strong> niż <InlineMath math={String(L)} /> i{' '}
        <strong>mniejszych</strong> niż <InlineMath math={String(R)} />.
      </p>
    );
  }
  if (phrasing === 'LE_RI') {
    // L < x ≤ R
    return (
      <p>
        Zbiór <InlineMath math={symbol} /> jest zbiorem liczb całkowitych{' '}
        <strong>większych</strong> niż <InlineMath math={String(L)} /> i{' '}
        <strong>nie większych</strong> niż <InlineMath math={String(R)} />.
      </p>
    );
  }
  if (phrasing === 'LI_RI') {
    // L ≤ x ≤ R
    return (
      <p>
        Zbiór <InlineMath math={symbol} /> jest zbiorem liczb całkowitych{' '}
        <strong>nie mniejszych</strong> niż <InlineMath math={String(L)} /> i{' '}
        <strong>nie większych</strong> niż <InlineMath math={String(R)} />.
      </p>
    );
  }
  // LE_RE: L < x < R
  return (
    <p>
      Zbiór <InlineMath math={symbol} /> jest zbiorem liczb całkowitych{' '}
      <strong>większych</strong> niż <InlineMath math={String(L)} /> i{' '}
      <strong>mniejszych</strong> niż <InlineMath math={String(R)} />.
    </p>
  );
}

function NaturalsText({ data }) {
  const { strict, bound, symbol } = data;
  return (
    <p>
      Zbiór <InlineMath math={symbol} /> jest zbiorem liczb naturalnych 
      {strict ? (
        <> <strong>mniejszych</strong> niż <InlineMath math={String(bound)} />.</>
      ) : (
        <> <strong>nie większych</strong> niż <InlineMath math={String(bound)} />.</>
      )}
    </p>
  );
}

/* ===== input ===== */
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <FiniteSetInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      placeholder={'np. { -2, -1, 0, 1 }'}
      ariaLabel="elementy zbioru"
      showPreview
      showKeyboard
    />
  );
}

/* ===== check ===== */
export function checkAnswer({ data }, userAnswer) {
  const parsed =
    userAnswer && typeof userAnswer === 'object'
      ? userAnswer.parsed
      : parseFiniteSetInput(userAnswer);

  if (!Array.isArray(parsed)) return false;

  const got = [...parsed].sort((a, b) => a - b);
  const expected = [...data.elems].sort((a, b) => a - b);

  if (got.length !== expected.length) return false;
  for (let i = 0; i < expected.length; i++) if (got[i] !== expected[i]) return false;
  return true;
}

/* ===== answer / user answer ===== */
export function renderAnswer({ data }) {
  return <InlineMath math={finiteSetToLatex(data.elems)} />;
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === 'object' ? (userAnswer?.raw ?? '') : (userAnswer ?? '');
  const parsed =
    userAnswer && typeof userAnswer === 'object'
      ? userAnswer.parsed
      : parseFiniteSetInput(userAnswer);

  if (parsed === undefined || raw.trim() === '') return <i>(brak odpowiedzi)</i>;
  if (parsed === null) return <i>(niepoprawny format)</i>;
  return <InlineMath math={finiteSetToLatex(parsed)} />;
}

/* ===== explanation ===== */
export function renderExplanation({ data }) {
  const { variant } = data;
  return variant === 'integers'
    ? <IntegersExplanation data={data} />
    : <NaturalsExplanation data={data} />;
}

function IntegersExplanation({ data }) {
  const { L, R, elems, phrasing, symbol } = data;

  const latexCond =
    phrasing === 'LI_RE' ? `${L} \\le x < ${R}` :
    phrasing === 'LE_RI' ? `${L} < x \\le ${R}` :
    phrasing === 'LI_RI' ? `${L} \\le x \\le ${R}` :
                           `${L} < x < ${R}`;

  const comment =
    phrasing === 'LI_RE' ? 'lewa granica włącznie, prawa bez' :
    phrasing === 'LE_RI' ? 'lewa bez, prawa włącznie' :
    phrasing === 'LI_RI' ? 'obie granice włącznie' :
                           'obie granice bez';

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Z treści mamy warunek{' '}
        <InlineMath math={latexCond} /> ({comment}).
      </p>
      <p>
        <strong>Krok 2:</strong> To oznacza, że do <InlineMath math={symbol} /> należą kolejno:
      </p>
      <BlockMath math={finiteSetToLatex(elems)} />
    </div>
  );
}

function NaturalsExplanation({ data }) {
  const { strict, bound, elems } = data;
  const rhs = strict ? `${bound - 1}` : `${bound}`;
  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Liczby naturalne zaczynamy od <InlineMath math={'0'} />.
        Warunek górny: <InlineMath math={strict ? `n < ${bound}` : `n \\le ${bound}`} />.
      </p>
      <p>
        <strong>Krok 2:</strong> Wypisujemy od <InlineMath math={'0'} /> do{' '}
        <InlineMath math={rhs} />:
      </p>
      <BlockMath math={finiteSetToLatex(elems)} />
    </div>
  );
}
